/* eslint-disable jsx-a11y/anchor-is-valid */
import { useRef } from 'react';
import cx from 'classnames';
import { CalendarIcon, SpannerIcon, SpeedometerIcon } from '@THC/components/Icons';
import { MDText } from 'i18n-react';
import { isUndefined } from 'lodash';
import Link from 'next/link';

import cypressIds from 'Components/cypress_ids.json';
import SectionHeading from 'Components/Insights/components/SectionHeading/SectionHeading';
import { checkModelIsNotOther } from 'Components/Insights/hooks/useGetMakesAndModels.helpers';
import { useInsightsRouter } from 'Components/Insights/hooks/useInsightsRouter';
import {
	triggerInsightsRecentSoldPaginationEvent,
	triggerInsightsRecentSoldVehicleClickedEvent,
} from 'Services/analytics/events';
import Pagination from 'Storybook/core/Pagination/Pagination';
import { DATE_FORMATS, formatDate, formatText, gbpCurrencyFormatter } from 'Utilities/formatters';
import { applyCypressData } from 'Utilities/helpers';
import type { UseInsightsRecentSoldResult } from 'Utilities/hooks/recentSoldPrices/useRecentSoldPrices';
import ROUTES from 'Utilities/routes';

import { ITEMS_PER_PAGE } from '../RecentSoldPrices/RecentSoldPrices.consts';
import { numberFieldHasValue, stringFieldHasValue } from '../RecentSoldPrices/RecentSoldPrices.helpers';

import LocalTexts from './RecentSoldPricesDataList.texts';

import styles from './RecentSoldPricesDataList.module.scss';

const LocalT = new MDText(LocalTexts);

type RecentSoldPricesDataListProps = {
	insightsRecentSold: UseInsightsRecentSoldResult;
	prettyMake: string;
	prettyModel: string;
};

export const RecentSoldPricesDataList: React.FC<RecentSoldPricesDataListProps> = ({
	insightsRecentSold,
	prettyMake: make,
	prettyModel: model,
}) => {
	const { data, isSuccess, status } = insightsRecentSold;

	const totalVehicles = data?.vehicles.length ?? 0;
	const disablePagination = !isSuccess;

	const { page } = useInsightsRouter();

	const vehicles = data?.vehicles.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

	const headingRef = useRef<HTMLHeadingElement>(null);

	const isOtherModel = !checkModelIsNotOther(model);

	return (
		<section>
			<SectionHeading ref={headingRef} className={styles.heading} text={LocalT.translate('heading')} />
			<ol>
				{!isUndefined(vehicles) &&
					vehicles.map((vehicle, itemIndex) => {
						return (
							<li
								key={itemIndex}
								className={styles.listItem}
								{...applyCypressData(`${cypressIds.insights.recentSoldPricesItem}-${itemIndex}`)}
							>
								<Link
									passHref
									as={ROUTES.VEHICLE.as(vehicle.enquiryId)}
									className={styles.clickableRow}
									href={`${ROUTES.VEHICLE.href}?vehicleId=${vehicle.enquiryId}`}
									onClick={() => {
										triggerInsightsRecentSoldVehicleClickedEvent({ enquiryId: vehicle.enquiryId });
									}}
								>
									{(numberFieldHasValue(vehicle.soldPrice) || stringFieldHasValue(vehicle.soldDate)) && (
										<div className={styles.soldPriceAndDate}>
											{(() => {
												const soldDate = stringFieldHasValue(vehicle.soldDate)
													? formatDate(vehicle.soldDate, DATE_FORMATS.DAY_MONTH_YEAR)
													: null;
												const soldPrice = numberFieldHasValue(vehicle.soldPrice)
													? gbpCurrencyFormatter({ maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(
															vehicle.soldPrice ?? 0,
													  )
													: null;

												if (stringFieldHasValue(vehicle.soldDate) && numberFieldHasValue(vehicle.soldPrice)) {
													return LocalT.translate('labels.soldPriceAndDate', { soldDate, soldPrice });
												}
												if (stringFieldHasValue(vehicle.soldDate)) {
													return <strong>{soldDate}</strong>;
												}
												if (numberFieldHasValue(vehicle.soldPrice)) {
													return <strong>{soldPrice}</strong>;
												}
												return null;
											})()}
										</div>
									)}
									<div className={styles.details}>
										<div className={cx(styles.detail, styles.vehicle)}>
											{make} {isOtherModel ? null : model} {vehicle.derivative}
										</div>
										{numberFieldHasValue(vehicle.manufactureYear) && (
											<div className={styles.detail}>
												<div className={styles.iconWrapper}>
													<CalendarIcon
														aria-hidden={false}
														aria-label={LocalT.translate('labels.regYear') as string}
														size="small"
													/>
												</div>
												{vehicle.manufactureYear}
											</div>
										)}
										{numberFieldHasValue(vehicle.mileage) && (
											<div className={styles.detail}>
												<div className={styles.iconWrapper}>
													<SpeedometerIcon
														aria-hidden={false}
														aria-label={LocalT.translate('labels.mileage') as string}
													/>
												</div>
												{formatText(vehicle.mileage)}
											</div>
										)}
										{stringFieldHasValue(vehicle.serviceHistory) && (
											<div className={styles.detail}>
												<div className={styles.iconWrapper}>
													<SpannerIcon
														aria-hidden={false}
														aria-label={LocalT.translate('labels.serviceHistory') as string}
													/>
												</div>
												{LocalT.translate(`serviceHistory.${vehicle.serviceHistory}`)}
											</div>
										)}
									</div>
								</Link>
							</li>
						);
					})}
			</ol>
			<Pagination
				textBetweenButtons
				containerClassName={styles.pagination}
				cypressId={cypressIds.insights.recentSoldPricesPagination}
				isDisabled={disablePagination}
				onClick={(pageNumber, direction) => {
					triggerInsightsRecentSoldPaginationEvent({ direction, page: pageNumber });
				}}
				page={page}
				queryStatus={status}
				recordsPerPage={ITEMS_PER_PAGE}
				// This offset is to accommodate the sticky header
				scrollToOffset={-80}
				scrollToRef={headingRef}
				textBetweenButtonsClassName={styles.paginationTextBetweenButtons}
				totalRecords={totalVehicles}
			/>
		</section>
	);
};
